import type { ReactNode } from "react";
import { createPortal } from "react-dom";

export interface PortalProps {
  children: ReactNode;
  /** Hôte du rendu. `document.body` par défaut. */
  container?: Element | null;
}

/**
 * Rend ses enfants ailleurs dans le document, sans changer l'arbre React.
 *
 * À utiliser pour tout ce qui doit se poser AU-DESSUS de la page et être calé
 * sur la fenêtre : voile, notification éphémère, menu contextuel maison.
 *
 * La raison est toujours la même, et elle ne se voit qu'une fois le bug arrivé :
 * `position: fixed` n'est calé sur la fenêtre que si aucun ancêtre ne porte
 * `transform`, `filter`, `perspective`, `backdrop-filter`, `will-change` sur
 * l'une de ces propriétés, ou `contain: paint`. Il suffit d'UN de ces
 * utilitaires — que ce système emploie largement pour garder les animations sur
 * le compositeur — pour que l'élément se recale silencieusement sur cette boîte
 * et se mette à défiler avec elle. Aucun `z-index` ne rattrape cela : le
 * problème n'est pas l'ordre d'empilement, c'est le bloc conteneur.
 *
 * Le portail supprime la condition au lieu de la contourner.
 */
export function Portal({ children, container }: PortalProps) {
  if (typeof document === "undefined") return null;
  return createPortal(children, container ?? document.body);
}
